// Third-party
import React, { ReactNode, CSSProperties } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import classNames from 'classnames';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';

// Components
import { ArrowNavigation } from '../buttons';

type Props = {
  children: ReactNode;
  extraStyles?: CSSProperties;
  className?: string;
  onScroll?: () => void;
  onResize?: () => void;
  onScrollX?: (atStart?: boolean) => void;
  onScrollY?: () => void;
  boundingRightElement?: Element;
  boundingLeftElement?: Element;
  extraLeftArrowClasses?: string;
  extraRightArrowClasses?: string;
  role?: string;
};

type State = {
  leftArrowDisabled: boolean;
  rightArrowDisabled: boolean;
};

const HORIZONTAL_SCROLL_DISTANCE = 400;

class GenericScrollbarContainer extends React.Component<Props, State> {
  private scrollContainerRef?: HTMLElement;

  readonly state: State = {
    leftArrowDisabled: true,
    rightArrowDisabled: true,
  };

  handleScroll = throttle(() => {
    if (this.props.onScroll) {
      this.props.onScroll();
    }
  }, 30);

  handleResize = throttle(() => {
    this.adjustArrows();

    if (this.props.onResize) {
      this.props.onResize();
    }
  }, 30);

  onScrollX = throttle(() => {
    if (this.props.onScrollX) {
      this.props.onScrollX();
    }

    this.adjustArrows();
  }, 30);

  onScrollY = throttle(() => {
    if (this.props.onScrollY) {
      this.props.onScrollY();
    }
  }, 30);

  componentDidMount() {
    if (this.props.onScroll) {
      window.addEventListener('scroll', this.handleScroll);
    }

    window.addEventListener('resize', this.handleResize);

    this.handleScroll();
    this.adjustArrows();
  }

  componentWillUnmount() {
    if (this.props.onScroll) {
      window.removeEventListener('scroll', this.handleScroll);
    }
    window.removeEventListener('resize', this.handleResize);
  }

  onArrowScroll = (distance: number) => {
    if (!this.scrollContainerRef) {
      return;
    }

    this.scrollContainerRef.scrollBy({ left: distance });
  };

  getScrollContainerRef = () => this.scrollContainerRef;

  adjustArrows = debounce(() => {
    const { boundingRightElement, boundingLeftElement } = this.props;

    if (!this.scrollContainerRef) {
      this.setState({
        rightArrowDisabled: true,
        leftArrowDisabled: true,
      });

      return;
    }

    const { left: containerLeft, right: containerRight } = this.scrollContainerRef.getBoundingClientRect();

    this.setState({
      rightArrowDisabled: !boundingRightElement || boundingRightElement.getBoundingClientRect().right <= containerRight,
      leftArrowDisabled: !boundingLeftElement || boundingLeftElement.getBoundingClientRect().left >= containerLeft,
    });
  }, 100);

  onXReachStart = () => {
    if (this.props.onScrollX) {
      this.props.onScrollX(true);
    }
  };

  render() {
    const { className, children, extraStyles, extraLeftArrowClasses, extraRightArrowClasses, role } = this.props;

    const { leftArrowDisabled, rightArrowDisabled } = this.state;

    const arrowLeftClasses = classNames(
      'generic-scrollbar-arrows',
      {
        disabled: leftArrowDisabled,
      },
      extraLeftArrowClasses,
    );

    const arrowRightClasses = classNames(
      'generic-scrollbar-arrows',
      {
        disabled: rightArrowDisabled,
      },
      extraRightArrowClasses,
    );

    return (
      <>
        <ArrowNavigation direction="left" className={arrowLeftClasses} onClick={() => this.onArrowScroll(-HORIZONTAL_SCROLL_DISTANCE)} />

        <PerfectScrollbar
          className={classNames('generic-scrollbar-container', className)}
          containerRef={ref => {
            this.scrollContainerRef = ref;
          }}
          // Enable horizontal scroll naturally only on touch devices -- Gives best UX this way.
          options={{ suppressScrollY: true }}
          onXReachStart={this.onXReachStart}
          onScrollX={this.onScrollX}
          onScrollY={this.onScrollY}
          style={extraStyles}
          role={role ?? 'scrollbar'}
        >
          {children}
        </PerfectScrollbar>

        <ArrowNavigation direction="right" className={arrowRightClasses} onClick={() => this.onArrowScroll(HORIZONTAL_SCROLL_DISTANCE)} />
      </>
    );
  }
}

export default GenericScrollbarContainer;
